import dayjs from "dayjs";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { saveAs } from "file-saver";
import {
  getOrderByClientNameAndMonthAndYears,
  getClientDebt,
} from "../utility/EndPointGoogleFirebase";

export async function handleDownload(selectedItem, selectedDate) {
  const selectedMonth = dayjs(selectedDate).month() + 1;
  const selectedYear = dayjs(selectedDate).year();

  const formatNote = (note) => {
    return note
      ? note
          .split("---")
          .map((entry) => {
            const parts = entry.split(";");
            if (parts.length === 2) {
              return `${parts[0]};${parts[1]}`;
            } else {
              return entry;
            }
          })
          .join("\n")
      : "";
  };

  try {
    // Legge il saldo precedente direttamente da Firebase
    const previousMonthTotal = await getClientDebt(selectedItem);

    const pdfDoc = await PDFDocument.create();
    const page = pdfDoc.addPage([600, 800]);

    // Aggiungi logo
    const logoUrl = "/img/logopdf.png";
    const logoImageBytes = await fetch(logoUrl).then((res) =>
      res.arrayBuffer()
    );
    const logoImage = await pdfDoc.embedPng(logoImageBytes);
    page.drawImage(logoImage, {
      x: 20,
      y: 720,
      width: 176.36,
      height: 82.74,
    });

    // Aggiungi info sotto al logo
    page.drawText(
      "Nuovo Forno dal 1966, di Matteo Lupis, via Europa 35, 89046 Marina di Gioiosa J. (RC)",
      {
        x: 20,
        y: 700,
        size: 9,
        color: rgb(0, 0, 0),
      }
    );

    page.drawText("Cel. 3286872410 - Tel 0964 369497 - P.IVA 03242870800", {
      x: 20,
      y: 685,
      size: 9,
      color: rgb(0, 0, 0),
    });

    // Ottieni i dati dal database
    const querySnapshot = await getOrderByClientNameAndMonthAndYears(
      selectedItem,
      String(selectedMonth),
      String(selectedYear)
    );

    const dayDataMap = {};
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const day = parseInt(data.giorno, 10);
      dayDataMap[day] = data;
    });

    // Font per il PDF
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);

    // Nome Cliente - Mese - Anno allineato al bordo destro
    const clientMonthYear = `${selectedItem} - ${getMonthAndYearFromNumber(selectedMonth)}`;
    const tableLeft = 20;
    const tableTop = 670;
    const columnWidths = [50, 70, 150, 80, 80, 80];
    const tableWidth = columnWidths.reduce((a, b) => a + b, 0);
    const lastCellRightX = tableLeft + tableWidth;

    page.drawText(clientMonthYear, {
      x: lastCellRightX - timesRomanFont.widthOfTextAtSize(clientMonthYear, 9),
      y: tableTop,
      size: 9,
      font: timesRomanFont,
      color: rgb(0, 0, 0),
    });

    // Inizio tabella
    let currentY = tableTop - 20;
    const headers = [
      "Giorno",
      "Pane",
      "Note",
      "Importo Note",
      "Acconto",
      "Totale Giorno",
    ];
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    let totaleMese = 0;

    const tableData = Array.from({ length: daysInMonth }, (_, index) => {
      const day = index + 1;
      const data = dayDataMap[day] || {};
      const formattedNote = formatNote(data.note || "");
      const pane = data.pane || "";
      const varie = data.varie || "";
      const acconto = data.acconto || "";
      const totaleGiorno = data.totale || "";

      totaleMese += parseFloat(totaleGiorno) || 0;

      return [
        day.toString(),
        pane.toString(),
        formattedNote,
        varie.toString(),
        acconto.toString(),
        totaleGiorno.toString(),
      ];
    });

    // Dati della tabella con intestazioni
    const fullTableData = [headers, ...tableData];

    fullTableData.forEach((row) => {
      let maxHeight = 18;
      row.forEach((cell, colIndex) => {
        const x = tableLeft + columnWidths.slice(0, colIndex).reduce((a, b) => a + b, 0);
        const y = currentY;
        const cellWidth = columnWidths[colIndex];
        const textWidth = timesRomanFont.widthOfTextAtSize(cell, 9);

        page.drawText(cell, {
          x: x + (cellWidth - textWidth) / 2,
          y: y - 15,
          size: 9,
          font: timesRomanFont,
          color: rgb(0, 0, 0),
        });

        page.drawRectangle({
          x,
          y: y - maxHeight,
          width: cellWidth,
          height: maxHeight,
          borderColor: rgb(0, 0, 0),
          borderWidth: 1,
        });
      });
      currentY -= 18;
    });

    // Totale Mese
    currentY -= 20;
    page.drawText(`Totale Mese: ${totaleMese.toFixed(2)} €`, {
      x: lastCellRightX - timesRomanFont.widthOfTextAtSize(`Totale Mese: ${totaleMese.toFixed(2)} €`, 10),
      y: currentY,
      size: 10,
      font: timesRomanFont,
      color: rgb(0, 0, 0),
    });

    // Saldo precedente — mostrato solo se > 0
    if (previousMonthTotal > 0) {
      currentY -= 20;
      const debtLabel = `Saldo Precedente: ${parseFloat(previousMonthTotal).toFixed(2)} €`;
      page.drawText(debtLabel, {
        x: lastCellRightX - timesRomanFont.widthOfTextAtSize(debtLabel, 10),
        y: currentY,
        size: 10,
        font: timesRomanFont,
        color: rgb(0, 0, 0),
      });

      currentY -= 20;
      const totaleLabel = `Totale: ${(totaleMese + parseFloat(previousMonthTotal)).toFixed(2)} €`;
      page.drawText(totaleLabel, {
        x: lastCellRightX - timesRomanFont.widthOfTextAtSize(totaleLabel, 10),
        y: currentY,
        size: 10,
        font: timesRomanFont,
        color: rgb(0, 0, 0),
      });
    }

    // Salva il PDF
    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    saveAs(blob, `${selectedItem}-${getMonthAndYearFromNumber(selectedMonth)}.pdf`);
  } catch (error) {
    console.error("Errore durante la generazione del PDF:", error);
  }
}

function getMonthAndYearFromNumber(number) {
  const date = new Date();
  date.setMonth(number - 1);
  const options = { month: "long", year: "numeric" };
  return date.toLocaleDateString("it-IT", options).toUpperCase();
}